import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  BadgeCheck,
  Ban,
  Check,
  CheckCircle2,
  GitBranch,
  MessageSquare,
  Play,
  Plus,
  RefreshCw,
  Rocket,
  Route,
  ShieldCheck,
  Target,
  Undo2,
} from 'lucide-react';
import {
  approveWorkRequest,
  createWorkRequest,
  createWorkRequestOutcome,
  deployWorkRequest,
  dispatchWorkRequest,
  getWorkRequestDeployment,
  getWorkRequestRollbackTargets,
  executeWorkRequestRollback,
  listProjects,
  listWorkRequests,
  patchWorkRequest,
  rejectWorkRequest,
  requestWorkRequestApproval,
  requestWorkRequestRollback,
  syncWorkRequestGitea,
  validateWorkRequest,
  type CreateWorkRequestInput,
  type ProjectResponse,
  type WorkRequestActivityEvent,
  type WorkRequestDeployment,
  type WorkRequestResponse,
  type WorkRequestRollbackTarget,
  type WorkRequestState,
  type WorkRequestSyncStatus,
  type WorkRequestType,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getProjectDisplayName } from '@/lib/repo-label';

const requestTypes: WorkRequestType[] = [
  'bug',
  'change_request',
  'feature',
  'design',
  'support',
  'deployment',
  'ops',
  'question',
  'other',
];

const stateOrder = [
  'new',
  'triaging',
  'planned',
  'in_progress',
  'blocked',
  'review',
  'delivered',
  'closed',
];

const syncStatuses: WorkRequestSyncStatus[] = ['pending', 'synced', 'failed', 'blocked'];

type TimelineEvent = WorkRequestActivityEvent & { at: string };

type ApprovalStatus = 'pending' | 'approved' | 'rejected';

/** Read the durable change-approval decision stored at `metadata.approval`, if any. */
function readApprovalStatus(request: WorkRequestResponse): ApprovalStatus | null {
  const approval = (request.metadata as { approval?: { status?: unknown } } | undefined)?.approval;
  const status = approval?.status;
  return status === 'pending' || status === 'approved' || status === 'rejected' ? status : null;
}

const SUBMITTABLE_FOR_APPROVAL: ReadonlySet<WorkRequestState> = new Set<WorkRequestState>([
  'new',
  'triaging',
  'planned',
  'blocked',
]);

/** True once the change-spine validation record is present (`metadata.validation`). */
function isValidated(request: WorkRequestResponse): boolean {
  const validation = (request.metadata as { validation?: { status?: unknown } } | undefined)
    ?.validation;
  return validation?.status === 'validated';
}

/** True once a rollback intent has been recorded (`metadata.rollback`). */
function isRollbackRequested(request: WorkRequestResponse): boolean {
  const rollback = (request.metadata as { rollback?: { status?: unknown } } | undefined)?.rollback;
  return rollback?.status === 'requested';
}

function activityEvents(request: WorkRequestResponse): TimelineEvent[] {
  const raw = request.metadata.activity;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((event): event is WorkRequestActivityEvent => {
      if (!event || typeof event !== 'object') return false;
      const candidate = event as Record<string, unknown>;
      return typeof candidate.type === 'string' && typeof candidate.message === 'string';
    })
    .map(event => ({
      ...event,
      at: event.createdAt ?? event.at ?? request.updated_at,
    }))
    .sort((a, b) => b.at.localeCompare(a.at));
}

function shortDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function chipClass(value: string): string {
  if (
    value === 'urgent' ||
    value === 'blocked' ||
    value === 'failed' ||
    value === 'approval-rejected'
  )
    return 'border-error/40 bg-error/10 text-error';
  if (value === 'high' || value === 'review' || value === 'pending' || value === 'approval-pending')
    return 'border-warning/40 bg-warning/10 text-warning';
  if (value === 'delivered' || value === 'synced' || value === 'approval-approved')
    return 'border-success/40 bg-success/10 text-success';
  return 'border-border bg-surface-muted text-text-secondary';
}

function Chip({
  children,
  value,
}: {
  children: React.ReactNode;
  value: string;
}): React.ReactElement {
  return (
    <span className={cn('inline-flex rounded border px-2 py-0.5 text-xs', chipClass(value))}>
      {children}
    </span>
  );
}

export function WorkbenchView({
  requests,
  selectedRequestId,
  onSelectRequest,
  onCreateClick,
  onAction,
  actionPending,
  deployments = [],
  rollbackTargets = [],
  onRequestRollback,
  onExecuteRollback,
}: {
  requests: WorkRequestResponse[];
  selectedRequestId: string | null;
  onSelectRequest: (id: string) => void;
  onCreateClick: () => void;
  onAction: (
    action:
      | 'discuss'
      | 'plan'
      | 'run'
      | 'outcome'
      | 'deliver'
      | 'sync'
      | 'request-approval'
      | 'approve'
      | 'reject'
      | 'deploy'
      | 'validate',
    request: WorkRequestResponse
  ) => void;
  deployments?: WorkRequestDeployment[];
  rollbackTargets?: WorkRequestRollbackTarget[];
  onRequestRollback?: (request: WorkRequestResponse, environment: string) => void;
  onExecuteRollback?: (request: WorkRequestResponse, real: boolean) => void;
  actionPending: boolean;
}): React.ReactElement {
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<'all' | WorkRequestState>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | WorkRequestType>('all');
  const [syncFilter, setSyncFilter] = useState<'all' | WorkRequestSyncStatus>('all');
  const selected = requests.find(r => r.id === selectedRequestId) ?? requests[0] ?? null;
  const filteredRequests = requests.filter(request => {
    const normalizedQuery = query.trim().toLowerCase();
    const matchesQuery =
      !normalizedQuery ||
      [
        request.id,
        request.title,
        request.description,
        request.customer_name,
        request.requester_name,
        request.requester_contact,
      ]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(normalizedQuery));
    return (
      matchesQuery &&
      (stateFilter === 'all' || request.state === stateFilter) &&
      (typeFilter === 'all' || request.request_type === typeFilter) &&
      (syncFilter === 'all' || request.sync_status === syncFilter)
    );
  });
  const grouped = stateOrder
    .map(state => ({ state, items: filteredRequests.filter(r => r.state === state) }))
    .filter(group => group.items.length > 0);
  const timeline = selected ? activityEvents(selected) : [];

  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-text-primary">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold tracking-normal">Operator Workbench</h1>
          <p className="text-sm text-text-secondary">
            Portfolio queue for customer requests, Gitea issues, conversations, outcomes, and
            delivery evidence.
          </p>
        </div>
        <Button onClick={onCreateClick} size="sm">
          <Plus />
          New request
        </Button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(320px,420px)_1fr] overflow-hidden">
        <section className="min-h-0 overflow-y-auto border-r border-border">
          <div className="sticky top-0 z-10 border-b border-border bg-background/95 px-4 py-3">
            <div className="text-sm font-medium">Portfolio queue</div>
            <div className="text-xs text-text-secondary">
              {filteredRequests.length} of {requests.length} active work request
              {requests.length === 1 ? '' : 's'}
            </div>
            <div className="mt-3 space-y-2">
              <div className="text-xs font-medium uppercase text-text-secondary">Queue filters</div>
              <input
                aria-label="Search requests"
                className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm"
                placeholder="Search requests"
                value={query}
                onChange={event => {
                  setQuery(event.target.value);
                }}
              />
              <div className="grid grid-cols-3 gap-2">
                <select
                  aria-label="State filter"
                  className="h-9 rounded-md border border-border bg-surface px-2 text-xs"
                  value={stateFilter}
                  onChange={event => {
                    setStateFilter(event.target.value as 'all' | WorkRequestState);
                  }}
                >
                  <option value="all">All states</option>
                  {stateOrder.map(state => (
                    <option key={state} value={state}>
                      {state.replaceAll('_', ' ')}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Type filter"
                  className="h-9 rounded-md border border-border bg-surface px-2 text-xs"
                  value={typeFilter}
                  onChange={event => {
                    setTypeFilter(event.target.value as 'all' | WorkRequestType);
                  }}
                >
                  <option value="all">All types</option>
                  {requestTypes.map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Sync filter"
                  className="h-9 rounded-md border border-border bg-surface px-2 text-xs"
                  value={syncFilter}
                  onChange={event => {
                    setSyncFilter(event.target.value as 'all' | WorkRequestSyncStatus);
                  }}
                >
                  <option value="all">All sync</option>
                  {syncStatuses.map(status => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="space-y-4 p-4">
            {grouped.length > 0 ? (
              grouped.map(group => (
                <div key={group.state}>
                  <div className="mb-2 flex items-center justify-between text-xs uppercase text-text-secondary">
                    <span>{group.state.replaceAll('_', ' ')}</span>
                    <span>{group.items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {group.items.map(request => (
                      <button
                        key={request.id}
                        type="button"
                        onClick={() => {
                          onSelectRequest(request.id);
                        }}
                        className={cn(
                          'w-full rounded-md border p-3 text-left transition-colors',
                          selected?.id === request.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border bg-surface hover:bg-surface-muted'
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium">{request.title}</div>
                            <div className="mt-1 truncate text-xs text-text-secondary">
                              {request.customer_name ?? 'Internal'}
                              {request.requester_name ? ` · ${request.requester_name}` : ''}
                            </div>
                          </div>
                          <Chip value={request.priority}>{request.priority}</Chip>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          <Chip value={request.request_type}>type:{request.request_type}</Chip>
                          <Chip value={request.sync_status}>
                            {request.sync_status === 'failed' ? 'Failed sync' : request.sync_status}
                          </Chip>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-md border border-border bg-surface p-4 text-sm text-text-secondary">
                No requests match the current filters.
              </div>
            )}
          </div>
        </section>

        <section className="min-h-0 overflow-y-auto">
          {selected ? (
            <div className="mx-auto max-w-5xl space-y-6 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-5">
                <div>
                  <div className="mb-2 flex flex-wrap gap-2">
                    <Chip value={selected.state}>{selected.state.replaceAll('_', ' ')}</Chip>
                    <Chip value={selected.request_type}>type:{selected.request_type}</Chip>
                    <Chip value={selected.priority}>priority:{selected.priority}</Chip>
                    {readApprovalStatus(selected) ? (
                      <Chip value={`approval-${readApprovalStatus(selected) ?? ''}`}>
                        approval:{readApprovalStatus(selected)}
                      </Chip>
                    ) : null}
                    {isValidated(selected) ? (
                      <Chip value="approval-approved">validated</Chip>
                    ) : null}
                  </div>
                  <h2 className="text-2xl font-semibold tracking-normal">{selected.title}</h2>
                  <p className="mt-2 max-w-3xl text-sm text-text-secondary">
                    {selected.description ?? 'No description provided.'}
                  </p>
                </div>
                {selected.gitea_issue_url ? (
                  <a
                    className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-surface-muted"
                    href={selected.gitea_issue_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <GitBranch className="h-4 w-4" />
                    Gitea issue #{selected.gitea_issue_number}
                  </a>
                ) : (
                  <Chip value={selected.sync_status}>
                    {selected.sync_error ?? 'No Gitea issue'}
                  </Chip>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-md border border-border bg-surface p-4">
                  <div className="text-xs uppercase text-text-secondary">Customer</div>
                  <div className="mt-2 text-sm font-medium">
                    {selected.customer_name ?? 'Internal'}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {selected.requester_name ?? 'No requester'}
                    {selected.requester_contact ? ` · ${selected.requester_contact}` : ''}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-surface p-4">
                  <div className="text-xs uppercase text-text-secondary">Conversation</div>
                  <div className="mt-2 text-sm font-medium">
                    {selected.conversation_id ? (
                      <Link
                        to={`/chat/${selected.conversation_id}`}
                        className="text-primary hover:underline"
                      >
                        {selected.conversation_id}
                      </Link>
                    ) : (
                      'Not linked'
                    )}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-surface p-4">
                  <div className="text-xs uppercase text-text-secondary">Evidence</div>
                  <div className="mt-2 text-sm font-medium">
                    {selected.linked_outcome_ids.length} outcomes ·{' '}
                    {selected.linked_workflow_run_ids.length} runs
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-md border border-border bg-surface p-4">
                  <div className="mb-3 text-sm font-medium">Outcomes</div>
                  <div className="flex flex-wrap gap-2">
                    {selected.linked_outcome_ids.length > 0 ? (
                      selected.linked_outcome_ids.map(id => (
                        <Link
                          key={id}
                          to={`/outcomes/${id}`}
                          className="rounded border border-border px-2 py-1 text-xs text-primary hover:bg-surface-muted"
                        >
                          {id}
                        </Link>
                      ))
                    ) : (
                      <span className="text-sm text-text-secondary">No linked outcomes</span>
                    )}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-surface p-4">
                  <div className="mb-3 text-sm font-medium">Workflow runs</div>
                  <div className="flex flex-wrap gap-2">
                    {selected.linked_workflow_run_ids.length > 0 ? (
                      selected.linked_workflow_run_ids.map(id => (
                        <Link
                          key={id}
                          to={`/workflows/runs/${id}`}
                          className="rounded border border-border px-2 py-1 text-xs text-primary hover:bg-surface-muted"
                        >
                          {id}
                        </Link>
                      ))
                    ) : (
                      <span className="text-sm text-text-secondary">No linked workflow runs</span>
                    )}
                  </div>
                </div>
              </div>

              {deployments.length > 0 ? (
                <div className="rounded-md border border-border bg-surface p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                    <Rocket className="h-4 w-4" />
                    Deployments
                  </div>
                  <div className="space-y-2">
                    {deployments.map(deployment => (
                      <div
                        key={deployment.manifestId}
                        className="flex flex-wrap items-center justify-between gap-2 border-b border-border/70 pb-2 text-sm last:border-0 last:pb-0"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <Chip value={deployment.environment}>{deployment.environment}</Chip>
                          <Chip
                            value={
                              deployment.status === 'succeeded' ? 'delivered' : deployment.status
                            }
                          >
                            {deployment.status}
                          </Chip>
                          <span className="text-text-secondary">{deployment.deploymentMode}</span>
                        </div>
                        <span className="font-mono text-xs text-text-tertiary">
                          {deployment.manifestId.slice(0, 12)} · {shortDate(deployment.deployedAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {rollbackTargets.length > 0 ? (
                <div className="rounded-md border border-border bg-surface p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                    <Undo2 className="h-4 w-4" />
                    Rollback
                    {isRollbackRequested(selected) ? (
                      <Chip value="approval-pending">requested</Chip>
                    ) : null}
                  </div>
                  <div className="space-y-3">
                    {rollbackTargets.map(target => (
                      <div key={target.targetManifestId} className="space-y-2 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Chip value={target.environment}>{target.environment}</Chip>
                            <span className="font-mono text-xs text-text-tertiary">
                              {target.currentManifestId.slice(0, 12)} →{' '}
                              {target.targetManifestId.slice(0, 12)}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={actionPending || isRollbackRequested(selected)}
                            title="Record a rollback intent and get the exact rollback command"
                            onClick={() => onRequestRollback?.(selected, target.environment)}
                          >
                            <Undo2 />
                            Request rollback
                          </Button>
                        </div>
                        <pre className="overflow-x-auto rounded border border-border bg-background p-2 text-xs text-text-secondary">
                          {target.command}
                        </pre>
                      </div>
                    ))}
                  </div>
                  {isRollbackRequested(selected) ? (
                    <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actionPending}
                        title="Ask the server to build the rollback command plan without executing"
                        onClick={() => onExecuteRollback?.(selected, false)}
                      >
                        <Undo2 />
                        Dry-run (server)
                      </Button>
                      <Button
                        size="sm"
                        disabled={actionPending}
                        title="Execute the rollback for real (requires ROLLBACK_EXECUTION_ENABLED on the server)"
                        onClick={() => onExecuteRollback?.(selected, true)}
                      >
                        <Rocket />
                        Execute rollback
                      </Button>
                    </div>
                  ) : null}
                  <p className="mt-2 text-xs text-text-tertiary">
                    Rollback re-promotes the prior artifact. Server execution is dry-run unless you
                    Execute (which needs the server-side gate enabled); the command above always
                    works as an operator-run fallback.
                  </p>
                </div>
              ) : null}

              <div className="rounded-md border border-border bg-surface p-4">
                <div className="mb-3 text-sm font-medium">Activity timeline</div>
                {timeline.length > 0 ? (
                  <div className="space-y-3">
                    {timeline.map((event, index) => (
                      <div
                        key={`${event.type}-${event.at}-${String(index)}`}
                        className="grid grid-cols-[120px_1fr] gap-3 border-b border-border/70 pb-3 last:border-0 last:pb-0"
                      >
                        <div className="text-xs text-text-secondary">{shortDate(event.at)}</div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Chip value={event.type}>{event.type.replaceAll('_', ' ')}</Chip>
                            {event.actor ? (
                              <span className="text-xs text-text-secondary">{event.actor}</span>
                            ) : null}
                          </div>
                          <div className="mt-1 text-sm">{event.message}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-text-secondary">No activity recorded yet</span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 border-t border-border pt-5">
                {readApprovalStatus(selected) === 'pending' ? (
                  <>
                    <Button
                      size="sm"
                      disabled={actionPending}
                      onClick={() => {
                        onAction('approve', selected);
                      }}
                    >
                      <Check />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={actionPending}
                      onClick={() => {
                        onAction('reject', selected);
                      }}
                    >
                      <Ban />
                      Reject
                    </Button>
                  </>
                ) : SUBMITTABLE_FOR_APPROVAL.has(selected.state) &&
                  readApprovalStatus(selected) !== 'approved' ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={actionPending}
                    onClick={() => {
                      onAction('request-approval', selected);
                    }}
                  >
                    <ShieldCheck />
                    Submit for approval
                  </Button>
                ) : null}
                {readApprovalStatus(selected) === 'approved' && !isValidated(selected) ? (
                  <Button
                    size="sm"
                    disabled={actionPending}
                    title="Dispatch maestro-deploy-verify for the linked outcome"
                    onClick={() => {
                      onAction('deploy', selected);
                    }}
                  >
                    <Rocket />
                    Deploy
                  </Button>
                ) : null}
                {deployments.some(d => d.status === 'succeeded') && !isValidated(selected) ? (
                  <Button
                    size="sm"
                    disabled={actionPending}
                    title="Confirm the problem is resolved and close the request"
                    onClick={() => {
                      onAction('validate', selected);
                    }}
                  >
                    <BadgeCheck />
                    Validate &amp; close
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onAction('discuss', selected);
                  }}
                >
                  <MessageSquare />
                  Discuss
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onAction('plan', selected);
                  }}
                >
                  <Route />
                  Plan
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onAction('run', selected);
                  }}
                >
                  <Play />
                  Run workflow
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!selected.primary_codebase_id || actionPending}
                  title={
                    !selected.primary_codebase_id ? 'Assign a project or codebase first' : undefined
                  }
                  onClick={() => {
                    onAction('outcome', selected);
                  }}
                >
                  <Target />
                  Create outcome
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onAction('deliver', selected);
                  }}
                >
                  <CheckCircle2 />
                  Mark delivered
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onAction('sync', selected);
                  }}
                >
                  <RefreshCw />
                  Sync Gitea
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-text-secondary">
              No work requests yet.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function CreateRequestDrawer({
  open,
  projects,
  onClose,
  onSubmit,
}: {
  open: boolean;
  projects: ProjectResponse[];
  onClose: () => void;
  onSubmit: (input: CreateWorkRequestInput) => void;
}): React.ReactElement | null {
  const [title, setTitle] = useState('');
  const [requestType, setRequestType] = useState<WorkRequestType>('bug');
  const [customerName, setCustomerName] = useState('');
  const [projectId, setProjectId] = useState('');
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
      <form
        className="h-full w-full max-w-md border-l border-border bg-background p-5 shadow-xl"
        onSubmit={event => {
          event.preventDefault();
          onSubmit({
            title,
            requestType,
            priority: 'normal',
            customerName: customerName || null,
            projectId: projectId || null,
          });
          setTitle('');
          setCustomerName('');
        }}
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold">New request</h2>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-text-secondary">Title</span>
          <input
            className="w-full rounded-md border border-border bg-surface px-3 py-2"
            value={title}
            onChange={e => {
              setTitle(e.target.value);
            }}
            required
          />
        </label>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-text-secondary">Type</span>
          <select
            className="w-full rounded-md border border-border bg-surface px-3 py-2"
            value={requestType}
            onChange={e => {
              setRequestType(e.target.value as WorkRequestType);
            }}
          >
            {requestTypes.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-text-secondary">Customer</span>
          <input
            className="w-full rounded-md border border-border bg-surface px-3 py-2"
            value={customerName}
            onChange={e => {
              setCustomerName(e.target.value);
            }}
          />
        </label>
        <label className="mb-5 block text-sm">
          <span className="mb-1 block text-text-secondary">Project</span>
          <select
            className="w-full rounded-md border border-border bg-surface px-3 py-2"
            value={projectId}
            onChange={e => {
              setProjectId(e.target.value);
            }}
          >
            <option value="">Internal only</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {getProjectDisplayName({ name: project.name })}
              </option>
            ))}
          </select>
        </label>
        <Button type="submit" disabled={!title.trim()}>
          <Plus />
          Create request
        </Button>
      </form>
    </div>
  );
}

export function WorkbenchPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data: requests = [] } = useQuery({
    queryKey: ['workRequests'],
    queryFn: listWorkRequests,
  });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: listProjects });
  const selectedId = useMemo(
    () => selectedRequestId ?? requests[0]?.id ?? null,
    [requests, selectedRequestId]
  );
  const { data: deployments = [] } = useQuery({
    queryKey: ['workRequestDeployment', selectedId],
    queryFn: () => (selectedId ? getWorkRequestDeployment(selectedId) : Promise.resolve([])),
    enabled: !!selectedId,
  });
  const { data: rollbackTargets = [] } = useQuery({
    queryKey: ['workRequestRollbackTarget', selectedId],
    queryFn: () => (selectedId ? getWorkRequestRollbackTargets(selectedId) : Promise.resolve([])),
    enabled: !!selectedId,
  });
  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['workRequests'] });
    await queryClient.invalidateQueries({ queryKey: ['workRequestDeployment'] });
    await queryClient.invalidateQueries({ queryKey: ['workRequestRollbackTarget'] });
  };
  const rollbackMutation = useMutation({
    mutationFn: ({ request, environment }: { request: WorkRequestResponse; environment: string }) =>
      requestWorkRequestRollback(request.id, environment),
    onSuccess: invalidate,
  });
  const executeRollbackMutation = useMutation({
    mutationFn: ({ request, real }: { request: WorkRequestResponse; real: boolean }) =>
      executeWorkRequestRollback(
        request.id,
        real ? { dryRun: false, confirm: true } : { dryRun: true }
      ),
    onSuccess: (result, { real }) => {
      if (result.dryRun) {
        window.alert(
          `Rollback dry-run — server would run:\n\n${(result.commands ?? []).join('\n')}`
        );
      } else {
        window.alert(
          `Rollback executed (exit ${String(result.exitCode)})${result.manifestId ? `\nmanifest: ${result.manifestId}` : ''}`
        );
      }
      if (real) void invalidate();
    },
    onError: (err: unknown) => {
      window.alert(`Rollback failed: ${(err as Error).message}`);
    },
  });
  const createMutation = useMutation({ mutationFn: createWorkRequest, onSuccess: invalidate });
  const actionMutation = useMutation({
    mutationFn: async ({ action, request }: { action: string; request: WorkRequestResponse }) => {
      if (action === 'deliver') {
        const deliveryNote = window.prompt('Delivery note');
        if (!deliveryNote?.trim()) return request;
        return patchWorkRequest(request.id, { state: 'delivered', deliveryNote });
      }
      if (action === 'sync') return syncWorkRequestGitea(request.id);
      if (action === 'outcome') return createWorkRequestOutcome(request.id);
      if (action === 'request-approval') return requestWorkRequestApproval(request.id);
      if (action === 'approve') {
        const note = window.prompt('Approval note (optional)') ?? undefined;
        return approveWorkRequest(request.id, note || undefined);
      }
      if (action === 'reject') {
        const reason = window.prompt('Reason for rejection (optional)') ?? undefined;
        return rejectWorkRequest(request.id, reason || undefined);
      }
      if (action === 'deploy') return deployWorkRequest(request.id);
      if (action === 'validate') {
        const note = window.prompt('Confirm resolution — what was verified as resolved?');
        if (!note?.trim()) return request;
        return validateWorkRequest(request.id, note.trim());
      }
      return dispatchWorkRequest(request.id, `${action} work request: ${request.title}`);
    },
    onSuccess: invalidate,
  });

  return (
    <>
      <WorkbenchView
        requests={requests}
        selectedRequestId={selectedId}
        onSelectRequest={setSelectedRequestId}
        onCreateClick={() => {
          setDrawerOpen(true);
        }}
        onAction={(action, request) => {
          actionMutation.mutate({ action, request });
        }}
        actionPending={
          actionMutation.isPending ||
          rollbackMutation.isPending ||
          executeRollbackMutation.isPending
        }
        deployments={Array.isArray(deployments) ? deployments : []}
        rollbackTargets={Array.isArray(rollbackTargets) ? rollbackTargets : []}
        onRequestRollback={(request, environment) => {
          rollbackMutation.mutate({ request, environment });
        }}
        onExecuteRollback={(request, real) => {
          if (
            real &&
            !window.confirm(
              'Execute this rollback for real? It re-promotes the prior artifact to the live target.'
            )
          ) {
            return;
          }
          executeRollbackMutation.mutate({ request, real });
        }}
      />
      <CreateRequestDrawer
        open={drawerOpen}
        projects={projects}
        onClose={() => {
          setDrawerOpen(false);
        }}
        onSubmit={input => {
          createMutation.mutate(input);
          setDrawerOpen(false);
        }}
      />
    </>
  );
}
